import React from 'react';
import { makeBtStyles } from '../Theme';
import moment from 'moment';

import EstimatedDeliveryDateOrStatus from '../components/EstimatedDeliveryDateOrStatus';
import { useTrackingData, useShipmentData, useBrandingData } from '../components/DataProviders';


const useStyles = makeBtStyles( theme => ({
  root: {
    marginTop: '42px',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      marginTop: '8px'
    }
  },
  statusMessage: ({ titleColor }) => ({
    fontSize: '24px',
    lineHeight: '26px',
    fontWeight: '600',
    color: titleColor,
    textTransform: 'none',
    '&.isDelivered, &.isInTransit': {
      color: titleColor
    }
  }),
  statusProgress: {
    marginTop: '12px',
    marginBottom: '25px',
    paddingRight: '10px',
    marginLeft: '-1px',
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      display: 'block'
    }
  },
  statusItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '32.8%',
    verticalAlign: 'top',
    height: '60px',
    color: 'white',
    paddingLeft: '24px',
    [theme.breakpoints.down('xs')]: {
      color: '#999',
      fontWeight: 'bold',
      borderTop: '1px solid #ddd',
      paddingLeft: '0',
      marginLeft: '80px',
      width: 'calc(100% - 80px)',
      '&:last-child': {
        borderBottom: '1px solid #ddd'
      }
    }
  },
  completeItem: {
    [theme.breakpoints.down('xs')]: {
      color: 'black'
    }
  },
  arrow: {
    background: '#A9A9A9',
    position: 'absolute',
    left: '0',
    top: '0',
    right: '0',
    bottom: '0',
    '&::before': {
      width: '0',
      height: '0',
      verticalAlign: 'middle',
      borderTop: '33px solid transparent',
      borderLeft: '33px solid #fff',
      borderBottom: '33px solid transparent',
      borderRight: '0 dotted',
      content: '""', // quotes inside quotes necessary we need "content" to have a value of ""
      position: 'absolute',
      top: '-3px',
      zIndex: '40',
      display: 'block',
      marginLeft: '-1px'
    },
    '&::after': {
      width: '0',
      height: '0',
      verticalAlign: 'middle',
      borderTop: '30px solid transparent',
      borderBottom: '30px solid transparent',
      borderLeft: '30px solid #A9A9A9',
      borderRight: '0 dotted',
      content: '""', // quotes inside quotes necessary we need "content" to have a value of ""
      position: 'absolute',
      zIndex: '50',
      top: '0',
      left: '100%',
      marginLeft: '-1px',
      display: 'block'
    },
    [theme.breakpoints.down('xs')]: {
      width: '60px',
      marginLeft: '-80px',
      '&::before': {
        borderLeft: '33px solid transparent',
        borderRight: '33px solid transparent',
        borderBottom: '33px solid transparent',
        borderTop: '20px solid #fff',
        margin: '3px 0 0 -3px'
      },
      '&::after': {
        width: '0',
        height: '0',
        borderLeft: '30px solid transparent',
        borderRight: '30px solid transparent',
        borderBottom: '30px solid transparent',
        borderTop: '19px solid #A9A9A9',
        margin: '-1px 0 0',
        top: '100%',
        left: '0'
      }
    },
  },
  completeArrow: ({ barColor }) => ({
    background: barColor,
    [theme.breakpoints.up('sm')]: {
      '&::after': {
        borderLeft: `30px solid ${barColor}`,
      }
    },
    [theme.breakpoints.down('xs')]: {
      '&::after': {
        borderTop: `19px solid ${barColor}`,
      }
    }
  }),
  statusItemText: {
    textAlign: 'center',
    fontSize: '18px',
    lineHeight: '18px',
    position: 'relative', // Ensure above arrow z-index-wise
    [theme.breakpoints.down('xs')]: {
      textAlign: 'left'
    }
  },
  statusItemDate: {
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: 'normal',
    position: 'relative', // Ensure above arrow z-index-wise
    [theme.breakpoints.down('xs')]: {
      textAlign: 'left'
    }
  }
}));


function useTimelineItems(shipment) {
  const { ordered_at } = useShipmentData();
  const {
    sourceEvent,
    latestUpdate,
    estimated_delivery_date,
    status_code,
    isDelivered,
    timestamp
  } = useTrackingData();

  // TODO: put a date on shipment (eg label_create_date) so we can always have one step
  const items = [];

  // Step 1
  if( ordered_at ) {
    items.push({ done: true, label: 'Ordered', date: ordered_at });
  }
  else if( sourceEvent ) {
    items.push({ done: true, label: 'Label Created', date: sourceEvent.carrier_occurred_at });
  }
  else {
    items.push({ done: false, label: 'Processing' });
  }

  // Step 2
  if( latestUpdate && latestUpdate !== sourceEvent ) {
    let [label,date] = isDelivered ? (
      ['In Transit', null]
    ):(
      [
        (status_code || '').toUpperCase() === 'AT' ? 'Attempted Delivery' : 'In Transit',
        latestUpdate.carrier_occurred_at
      ]
    );
    items.push({ done: true, label, date });
  }
  else {
    items.push({ done: false, label: 'In Transit' });
  }

  // Step 3
  if( isDelivered ) {
    items.push({ done: true, label: 'Delivered', date: timestamp });
  }
  else {
    items.push({ done: false, label: 'Expected Delivery', date: estimated_delivery_date });
  }

  return items;
}


export default function StatusRow() {
  const { colors } = useBrandingData();
  const classes = useStyles({ titleColor: colors && colors.primary, barColor: colors && colors.secondary });
  const status_items = useTimelineItems();

  const formatDate = (date) => moment(date).format('dddd - MMM D');

  return (
    <div className={classes.root}>
      <EstimatedDeliveryDateOrStatus className={classes.statusMessage}>
        { ({ date, text, status }) => (
          (date && (status === 'isInTransit' || status === 'isAccepted')) ? (
            <>Estimated Delivery: {moment(date).format('dddd, MMMM D')}</>
          ):(
            text
          )
        )}
      </EstimatedDeliveryDateOrStatus>

      <div className={classes.statusProgress}>
        { status_items.map( item => (
          <div className={`${classes.statusItem} ${item.done ? classes.completeItem : ''}`} key={item.label}>
            <div className={`${classes.arrow} ${item.done ? classes.completeArrow : ``}`}></div>
            <div className={classes.statusItemText}>{item.label}</div>
            { item.date &&
              <div className={classes.statusItemDate}>{formatDate(item.date)}</div>
            }
          </div>
        ))}
      </div>
    </div>
  );
}